import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';

const FilterToolbar = ({ 
  searchTerm, 
  onSearchChange, 
  filters, 
  onFilterChange, 
  totalGuests, 
  filteredCount,
  onClearFilters 
}) => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const dietaryTypes = [
    { id: 'allergy', label: 'Allergies', color: 'text-error' },
    { id: 'intolerance', label: 'Intolerances', color: 'text-warning' },
    { id: 'preference', label: 'Preferences', color: 'text-secondary' }
  ];

  const activityFilters = [
    { id: 'recent', label: 'Recent Guests (30 days)' },
    { id: 'frequent', label: 'Frequent Guests (5+ visits)' },
    { id: 'new', label: 'New Guests (1-2 visits)' }
  ];

  const hasActiveFilters = () => {
    return filters?.dietaryTypes?.length > 0 || 
           filters?.familyGroups?.length > 0 || 
           filters?.activity?.length > 0 ||
           searchTerm?.length > 0;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 mb-6">
      {/* Search and Main Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Icon 
              name="Search" 
              size={16} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
            />
            <Input
              type="search"
              placeholder="Search guests by name or email..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e?.target?.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex items-center space-x-2">
          <Button
            variant={isFilterPanelOpen ? "default" : "outline"}
            size="sm"
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            iconName="Filter"
            iconPosition="left"
          >
            Filters
            {hasActiveFilters() && (
              <span className="ml-1 px-1.5 py-0.5 bg-primary text-primary-foreground text-xs rounded-full">
                {filters?.dietaryTypes?.length + filters?.familyGroups?.length + filters?.activity?.length}
              </span>
            )}
          </Button>

          {hasActiveFilters() && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      {/* Results Count */}
      <div className="mt-3 text-sm text-muted-foreground">
        Showing {filteredCount} of {totalGuests} guests
        {hasActiveFilters() && (
          <span className="ml-2 text-primary">
            (filtered)
          </span>
        )}
      </div>
      {/* Filter Panel */}
      {isFilterPanelOpen && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Dietary Restrictions */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Dietary Restrictions</h4>
              <div className="space-y-2">
                {dietaryTypes?.map((type) => (
                  <Checkbox
                    key={type?.id}
                    label={
                      <span className={`flex items-center space-x-2 ${type?.color}`}>
                        <Icon 
                          name={
                            type?.id === 'allergy' ? 'AlertTriangle' :
                            type?.id === 'intolerance' ? 'AlertCircle' : 'Heart'
                          } 
                          size={14} 
                        />
                        <span>{type?.label}</span>
                      </span>
                    }
                    checked={filters?.dietaryTypes?.includes(type?.id)}
                    onChange={(e) => {
                      const newTypes = e?.target?.checked
                        ? [...filters?.dietaryTypes, type?.id]
                        : filters?.dietaryTypes?.filter(t => t !== type?.id);
                      onFilterChange({ ...filters, dietaryTypes: newTypes });
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Family Groups */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Family Groups</h4>
              <div className="space-y-2">
                <Checkbox
                  label="Show only family members"
                  checked={filters?.familyGroups?.includes('has_family')}
                  onChange={(e) => {
                    const newGroups = e?.target?.checked
                      ? [...filters?.familyGroups, 'has_family']
                      : filters?.familyGroups?.filter(g => g !== 'has_family');
                    onFilterChange({ ...filters, familyGroups: newGroups });
                  }}
                />
                <Checkbox
                  label="Show only individuals"
                  checked={filters?.familyGroups?.includes('no_family')}
                  onChange={(e) => {
                    const newGroups = e?.target?.checked
                      ? [...filters?.familyGroups, 'no_family']
                      : filters?.familyGroups?.filter(g => g !== 'no_family');
                    onFilterChange({ ...filters, familyGroups: newGroups });
                  }}
                />
              </div>
            </div>

            {/* Activity Filters */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3">Activity</h4>
              <div className="space-y-2">
                {activityFilters?.map((activity) => (
                  <Checkbox
                    key={activity?.id}
                    label={activity?.label}
                    checked={filters?.activity?.includes(activity?.id)}
                    onChange={(e) => {
                      const newActivity = e?.target?.checked
                        ? [...filters?.activity, activity?.id]
                        : filters?.activity?.filter(a => a !== activity?.id);
                      onFilterChange({ ...filters, activity: newActivity });
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterToolbar;